import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle } from 'lucide-react';
import GoogleLoginButton from '../components/Auth/GoogleLoginButton';
import AppleLoginButton from '../components/Auth/AppleLoginButton';
import { RECRUITER_DEMO_URL } from '../constants/recruiterContact';
import { CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION } from '../config/consent';
import { getAffiliateCode } from '../lib/affiliate';

const Signup: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (password !== confirmPassword) {
      setError(t('signup.errorPasswordMismatch'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('signup.errorPasswordLength'));
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setError(t('signup.errorTermsNotAccepted'));
      setLoading(false);
      return;
    }

    try {
      // Add consent metadata to Supabase user_metadata
      const signupData: Record<string, any> = {
        terms_accepted_at: new Date().toISOString(),
        terms_version: CURRENT_TERMS_VERSION,
        privacy_accepted_at: new Date().toISOString(),
        privacy_version: CURRENT_PRIVACY_VERSION
      };

      // Attach partner affiliate code (first-touch) if the user arrived via a
      // partner link. Internal-only; re-validated + persisted server-side.
      const affiliateCode = getAffiliateCode();
      if (affiliateCode) {
        signupData.affiliate_code = affiliateCode;
        signupData.affiliate_captured_at = new Date().toISOString();
      }

      const { error } = await signUp(email, password, {
        data: signupData
      });
      if (error) {
        setError(error.message || t('signup.errorCreateFailed'));
      } else {
        // Successful signup - redirect to email confirmation page
        // User needs to confirm email before logging in
        // Profile will be automatically created with is_recruiter=false via lazy creation after confirmation
        navigate('/email-confirmation');
      }
    } catch (err: any) {
      setError(err.message || t('signup.errorUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full">
        <h2 className="text-center text-xl sm:text-3xl font-bold text-slate-900 px-4 sm:px-0">
          {(() => {
            const title = t('signup.explorer.title');
            const match = title.match(/\*(.+?)\*/);
            if (match) {
              const idx = title.indexOf(match[0]);
              return <>{title.slice(0, idx)}<span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">{match[1]}</span>{title.slice(idx + match[0].length)}</>;
            }
            return <>{title}</>;
          })()}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm border border-slate-200 sm:rounded-2xl sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Social Signup */}
          <div className="space-y-3">
            <GoogleLoginButton disabled={loading} />
            <AppleLoginButton disabled={loading} />
          </div>
          
          {/* Terms text */}
          <p className="text-[10px] text-gray-500 mt-3 text-center">
            {t('signup.socialTermsText', 'By continuing, you agree to our')}{' '}
            <Link to="/terms" className="underline hover:text-gray-700">{t('signup.termsLink')}</Link>
            {' '}{t('signup.termsAnd')}{' '}
            <Link to="/privacy" className="underline hover:text-gray-700">{t('signup.privacyLink')}</Link>
            {t('signup.socialTermsSuffix', '')}
          </p>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('signup.orContinueWith', 'or')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('signup.explorer.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder={t('signup.explorer.emailPlaceholder')}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('signup.passwordLabel')}
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder={t('signup.passwordPlaceholder')}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('signup.confirmPasswordLabel')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder={t('signup.confirmPasswordPlaceholder')}
                disabled={loading}
              />
            </div>

            {/* Terms & Privacy Consent */}
            <div className="flex items-start">
              <input
                id="terms"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                disabled={loading}
              />
              <label htmlFor="terms" className="ml-2 text-[12px] text-gray-700">
                {t('signup.termsText')}{' '}
                <Link to="/terms" target="_blank" className="text-pink-600 hover:text-pink-700 underline">
                  {t('signup.termsLink')}
                </Link>{' '}
                {t('signup.termsAnd')}{' '}
                <Link to="/privacy" target="_blank" className="text-pink-600 hover:text-pink-700 underline">
                  {t('signup.privacyLink')}
                </Link>
                <span className="text-red-600 ml-1">*</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !termsAccepted}
              className="w-full flex justify-center py-2.5 px-4 rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? t('signup.creatingButton') : t('signup.createButton')}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              {t('signup.recruiterInvitedIntro')}{' '}
              <a
                href={RECRUITER_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-pink-600 hover:text-pink-700 transition-colors"
              >
                {t('signup.recruiterInvitedCta')} →
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
